import PropTypes from 'prop-types';
import * as React from 'react';
import { RenderComp } from '../core/render-comp.component.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { PopoverContext } from './popover.context.js';
const propTypes = {
  expanded: PropTypes.bool,
  position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  // Correct way for ref in props: https://stackoverflow.com/questions/48007326/what-is-the-correct-proptype-for-a-ref-in-react/51127130#51127130
  eventContainerRef: PropTypes.oneOfType([
  // Either a function
  PropTypes.func,
  // Or the instance of a DOM native element (see the note about SSR)
  // FIXME(Not a priority): Below is commented as `Element is not defined in SSR and we need to provide a shim
  // PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  PropTypes.shape({
    current: PropTypes.any
  })])
};
const propKeysToRemove = Object.keys(propTypes);
const Popover = renderWithRef('Popover', propTypes, (props, ref) => {
  const {
    expanded,
    position,
    children,
    eventContainerRef
  } = props;
  const [isExpanded, setIsExpanded] = React.useState(expanded || false);
  const [isButtonClick, setIsButtonClick] = React.useState(false);
  const launcherRef = React.useRef(null);
  const handleClick = event => {
    // Was the click that triggered this within the Popover?
    const eventPath = event.composedPath && event.composedPath() || [];
    let popoverClick = false;
    let closerClick = false;
    eventPath.forEach(currentElement => {
      var _currentElement_classList, _currentElement_classList1;
      if ((_currentElement_classList = currentElement.classList) === null || _currentElement_classList === void 0 ? void 0 : _currentElement_classList.contains('swan-popover')) {
        popoverClick = true;
      }
      if ((_currentElement_classList1 = currentElement.classList) === null || _currentElement_classList1 === void 0 ? void 0 : _currentElement_classList1.contains('swan-popover-close')) {
        closerClick = true;
      }
    });
    // Only close if we clicked outside the Popover, or on a PopoverCloser
    if (event && event.type === 'click' && isExpanded && !isButtonClick && (!popoverClick || closerClick)) {
      setIsExpanded(false);
    }
    setIsButtonClick(false);
  };
  const handleKeyPress = event => {
    if (event.key === 'Escape') {
      setIsExpanded(false);
    }
  };
  const onRequestExpandedChange = newExpanded => {
    setIsButtonClick(true);
    setIsExpanded(newExpanded);
  };
  const currentElem = typeof eventContainerRef !== 'function' ? eventContainerRef === null || eventContainerRef === void 0 ? void 0 : eventContainerRef.current : null;
  React.useEffect(() => {
    const element = currentElem || document;
    const addPopoverEventListeners = () => {
      element.addEventListener('click', handleClick);
      element.addEventListener('keydown', handleKeyPress);
      setIsButtonClick(false);
    };
    const removePopoverEventListeners = () => {
      element.removeEventListener('click', handleClick);
      element.removeEventListener('keydown', handleKeyPress);
    };
    if (isExpanded) {
      addPopoverEventListeners();
    } else {
      removePopoverEventListeners();
    }
    return () => removePopoverEventListeners();
  }, [isExpanded, isButtonClick, currentElem]);
  const classNames = ['swan-popover-container'];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RenderComp, {
    root: "div",
    forwardedRef: ref,
    classNames: classNames,
    props: props,
    propKeysToRemove: propKeysToRemove
  }, /*#__PURE__*/React.createElement(PopoverContext.Provider, {
    value: {
      expanded: isExpanded,
      position,
      onRequestExpandedChange,
      hidePopover: handleClick,
      launcherRef
    }
  }, children)));
});
Popover.defaultProps = {
  expanded: false,
  position: 'top'
};
export { Popover };